/**
 * Admin user panel: make someone an intern (location, lead, 18+), see their
 * internship if they are one, and make someone a program director (Tyler
 * Clark's proposal: an Intern toggle beside BAM Coach and Certification).
 */
import React, { useEffect, useState } from "react";
import { GraduationCap, Check } from "lucide-react";
import { GOLD } from "../constants/theme";
import { internAdmin, refreshInternBadges } from "./internApi";
import { STAGE_LABEL } from "./internLogic";
import { Select } from "../shared/Pickers";

const FILMDESK_URL = "https://platform.byanymeanscoaches.com/#/filmdesk";

export function InternAdminSection({ C, dark, profile, go }) {
  const [enr, setEnr] = useState(undefined);
  const [locations, setLocations] = useState([]);
  const [loc, setLoc] = useState("");
  const [lead, setLead] = useState("");
  const [adult, setAdult] = useState(false);
  const [director, setDirector] = useState(false);
  // A director runs the program; enrolling one as an intern is rare, so it's tucked away.
  const [showEnroll, setShowEnroll] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [program, setProgram] = useState("in_person");   // in_person | remote (remote = film interns)
  const [result, setResult] = useState(null);            // { onboard_url, invited } after a remote enroll
  const [copied, setCopied] = useState(false);
  const [certCap, setCertCap] = useState(null);          // remote intern's cert unit cap; null = all units

  useEffect(() => {
    let off = false;
    (async () => {
      try {
        const [person, locs] = await Promise.all([internAdmin("person", { user_id: profile.id }), internAdmin("locations")]);
        if (off) return;
        setEnr(person.enrollment); setDirector(!!person.is_intern_director); setCertCap(person.cert_unit_cap ?? null); setLocations(locs.locations || []);
      } catch { if (!off) setEnr(null); /* not set up yet */ }
    })();
    return () => { off = true; };
  }, [profile.id]);

  const location = locations.find(l => l.id === loc);
  useEffect(() => { setLead(location?.leads?.[0]?.id || ""); }, [loc]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async (fn, ok) => {
    setBusy(true); setMsg(null);
    try { await fn(); setMsg({ ok: true, text: ok }); }
    catch (e) { setMsg({ ok: false, text: e.message || "Something went wrong" }); }
    setBusy(false);
  };

  const enroll = () => run(async () => {
    const payload = program === "remote"
      ? { program: "remote", user_id: profile.id, confirmed_18_plus: adult }
      : { org_id: loc, user_id: profile.id, lead_id: lead || undefined, confirmed_18_plus: adult };
    const r = await internAdmin("enroll", payload);
    if (program === "remote") setResult({ onboard_url: r.onboard_url, invited: r.invited });
    const after = await internAdmin("person", { user_id: profile.id });
    setEnr(after.enrollment); setCertCap(after.cert_unit_cap ?? null); refreshInternBadges();
  }, program === "remote"
    ? "Enrolled as a remote film intern. First certification unit unlocked."
    : "Enrolled. Certification unlocked, platform access on, and they're in the intern chats.");

  const setCap = (cap) => run(async () => {
    const r = await internAdmin("cert_cap", { user_id: profile.id, cap });
    setCertCap(r.cert_unit_cap ?? null);
  }, "Certification access updated.");

  const active = enr && enr.status !== "exited";
  const enrLoc = active ? locations.find(l => l.id === enr.org_id) : null;
  const inp = { width: "100%", minHeight: 40, padding: "8px 10px", borderRadius: 8, border: `1px solid ${C.border}`, background: dark ? "#0E0E0E" : "#fff", color: C.text, fontSize: 13, boxSizing: "border-box" };
  const capBtn = { padding: "7px 11px", borderRadius: 8, border: `1px solid ${GOLD}`, background: `${GOLD}18`, color: GOLD, fontSize: 12, fontWeight: 700, cursor: busy ? "default" : "pointer" };
  const capBtnMuted = { padding: "7px 11px", borderRadius: 8, border: `1px solid ${C.border}`, background: "transparent", color: C.textMid, fontSize: 12, fontWeight: 700, cursor: busy ? "default" : "pointer" };

  return (
    <div style={{ marginTop: 20, paddingTop: 16, borderTop: `1px solid ${C.border}` }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <div style={{ width: 22, height: 22, borderRadius: 6, background: `${GOLD}14`, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <GraduationCap size={11} color={GOLD}/>
        </div>
        <span style={{ fontSize: 11, fontWeight: 800, color: C.text, textTransform: "uppercase", letterSpacing: .8 }}>BAM Intern</span>
      </div>

      {enr === undefined ? <div style={{ fontSize: 12, color: C.textDim }}>Loading…</div> : active ? (
        <div style={{ fontSize: 12.5, color: C.text, lineHeight: 1.55 }}>
          Intern at <b>{enrLoc?.name || "their location"}</b>
          {enrLoc?.leads?.find(l => l.id === enr.lead_id) && <> · Lead: {enrLoc.leads.find(l => l.id === enr.lead_id).name}</>}
          {" · "}{STAGE_LABEL[enr.stage]}{enr.status !== "active" ? ` (${enr.status})` : ""}
          <div style={{ marginTop: 8, display: "flex", gap: 6, flexWrap: "wrap" }}>
            <button onClick={() => go?.("interns")} style={{ padding: "8px 12px", borderRadius: 8, border: `1px solid ${C.border}`, background: "transparent", color: C.textMid, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
              Manage in the Intern Hub
            </button>
            {enr.program === "remote" && (
              <button type="button"
                onClick={() => { try { navigator.clipboard.writeText(FILMDESK_URL); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* clipboard blocked */ } }}
                style={{ padding: "8px 12px", borderRadius: 8, border: "none", background: GOLD, color: "#111", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                {copied ? "Copied" : "Copy Film Desk link"}
              </button>
            )}
          </div>
          {enr.program === "remote" && <div style={{ fontSize: 11, color: C.textDim, marginTop: 6, lineHeight: 1.5 }}>Send them this to get into Film Desk. A brand new intern sets a password from their invite email first.</div>}
          {enr.program === "remote" && (
            <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${C.border}` }}>
              <div style={{ fontSize: 11.5, color: C.textMid, marginBottom: 6 }}>
                Certification: {certCap == null ? "all units unlocked" : certCap <= 1 ? "unit 1 only" : `units 1 to ${certCap}`}
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {certCap != null && <button type="button" onClick={() => setCap((certCap || 1) + 1)} disabled={busy} style={capBtn}>Unlock next unit</button>}
                {certCap != null && <button type="button" onClick={() => setCap(null)} disabled={busy} style={capBtn}>Unlock all units</button>}
                {certCap == null && <button type="button" onClick={() => setCap(1)} disabled={busy} style={capBtnMuted}>Re-lock to unit 1</button>}
              </div>
            </div>
          )}
        </div>
      ) : director && !showEnroll ? (
        <button onClick={() => setShowEnroll(true)} style={{ background: "transparent", border: "none", padding: "4px 0", color: C.textDim, fontSize: 12, fontWeight: 700, cursor: "pointer", textDecoration: "underline" }}>
          Enroll them as an intern instead
        </button>
      ) : (
        <div style={{ display: "grid", gap: 8 }}>
          <div style={{ display: "flex", gap: 6 }}>
            {[["in_person", "In person"], ["remote", "Remote film"]].map(([val, label]) => (
              <button key={val} type="button" onClick={() => setProgram(val)}
                style={{ flex: 1, padding: "7px 10px", borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: "pointer",
                  border: `1px solid ${program === val ? GOLD : C.border}`, background: program === val ? `${GOLD}18` : "transparent", color: program === val ? GOLD : C.textMid }}>
                {label}
              </button>
            ))}
          </div>
          {program === "in_person" ? (locations.length === 0 ? (
            <div style={{ fontSize: 11.5, color: C.textDim, lineHeight: 1.5 }}>
              No locations yet. Create one in People › Organizations with type Location, then come back. (Needs docs/intern-hub.sql run once.)
            </div>
          ) : (
            <>
              <Select value={loc} onChange={e => setLoc(e.target.value)} style={inp} aria-label="Location">
                <option value="">Choose a location…</option>
                {locations.map(l => <option key={l.id} value={l.id}>{l.name}{l.state_code ? ` (${l.state_code})` : ""}</option>)}
              </Select>
              {location && (
                <Select value={lead} onChange={e => setLead(e.target.value)} style={inp} aria-label="Lead">
                  {(location.leads || []).map(l => <option key={l.id} value={l.id}>Lead: {l.name}</option>)}
                  {!location.leads?.length && <option value="">No lead at this location yet</option>}
                </Select>
              )}
            </>
          )) : (
            <div style={{ fontSize: 11.5, color: C.textDim, lineHeight: 1.5 }}>
              Remote film intern: no location needed. They get Film Desk access and the first certification unit, with more unlocked as they go.
            </div>
          )}
          <label style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 12, color: C.text, lineHeight: 1.45, cursor: "pointer" }}>
            <input type="checkbox" checked={adult} onChange={e => setAdult(e.target.checked)} style={{ marginTop: 2 }}/>
            Confirmed 18 or older
          </label>
          <button onClick={enroll} disabled={busy || !adult || (program === "in_person" && !loc)}
            style={{ padding: "10px 14px", borderRadius: 9, fontSize: 12.5, fontWeight: 800, border: "none", background: GOLD, color: "#111",
              cursor: (busy || !adult || (program === "in_person" && !loc)) ? "default" : "pointer", opacity: (busy || !adult || (program === "in_person" && !loc)) ? .5 : 1 }}>
            {busy ? "Enrolling…" : program === "remote" ? "Make remote intern" : "Make intern"}
          </button>
        </div>
      )}

      {result && (
        <div style={{ marginTop: 10, padding: 10, borderRadius: 8, border: `1px solid ${C.border}`, background: dark ? "#0E0E0E" : "#fff", display: "grid", gap: 6 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>Send this so they can start in Film Desk:</div>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <input readOnly value={result.onboard_url || ""} onFocus={e => e.target.select()} style={{ ...inp, minHeight: 34, fontSize: 12 }}/>
            <button type="button" onClick={() => { try { navigator.clipboard.writeText(result.onboard_url || ""); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* clipboard blocked */ } }}
              style={{ padding: "8px 12px", borderRadius: 8, border: "none", background: GOLD, color: "#111", fontSize: 12, fontWeight: 800, cursor: "pointer", whiteSpace: "nowrap" }}>
              {copied ? "Copied" : "Copy link"}
            </button>
          </div>
          {result.invited && <div style={{ fontSize: 11.5, color: C.textDim, lineHeight: 1.5 }}>They will also get an email to set a password first, so send both.</div>}
        </div>
      )}

      <button onClick={() => run(async () => { await internAdmin("set_director", { user_id: profile.id, on: !director }); setDirector(!director); }, director ? "No longer a program director." : "Now a program director: sees every location in the Intern Hub.")}
        disabled={busy}
        style={{ width: "100%", marginTop: 12, padding: "10px 14px", borderRadius: 9, fontSize: 12.5, fontWeight: 700, cursor: busy ? "default" : "pointer",
          border: director ? `1px solid ${GOLD}` : `1px solid ${C.border}`, background: director ? `${GOLD}18` : "transparent", color: director ? GOLD : C.textMid,
          display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
        {director ? <><Check size={12}/> Intern program director (click to remove)</> : <>Make intern program director</>}
      </button>
      <div style={{ fontSize: 11, color: C.textDim, marginTop: 8, lineHeight: 1.5 }}>
        Directors (Tyler, Silva) see every location's interns, without the rest of admin.
      </div>
      {msg && <div role="status" style={{ marginTop: 8, fontSize: 12, color: msg.ok ? GOLD : "#F87171" }}>{msg.text}</div>}
    </div>
  );
}

export default InternAdminSection;
